import type { FunctionSignature } from '@/lib/api/problems';

/**
 * Languages that have a generated function stub + judge harness.
 *
 * C is here but is additionally signature-gated: see supportsC(). Unlike the
 * others it cannot fall back to a dynamic type (`auto`, `Object`,
 * `interface{}`), so a signature outside the mapped vocabulary would produce a
 * stub that does not compile — those problems drop C from the dropdown
 * instead, mirroring cSupportsSignature() in the judge's c_harness.go.
 */
export const STUB_LANGUAGES = ['python', 'javascript', 'cpp', 'java', 'go', 'c'];

const PY_TYPES: Record<string, string> = {
  int: 'int', long: 'int', double: 'float', bool: 'bool', string: 'str', char: 'str',
  'int[]': 'List[int]', 'long[]': 'List[int]', 'double[]': 'List[float]',
  'bool[]': 'List[bool]', 'string[]': 'List[str]', 'char[]': 'List[str]',
  'int[][]': 'List[List[int]]', 'long[][]': 'List[List[int]]', 'double[][]': 'List[List[float]]',
  'bool[][]': 'List[List[bool]]', 'string[][]': 'List[List[str]]', 'char[][]': 'List[List[str]]',
  ListNode: 'Optional[ListNode]',
};

const JS_SCALARS: Record<string, string> = {
  int: 'number', long: 'number', double: 'number', bool: 'boolean', string: 'string', char: 'string',
  ListNode: 'ListNode',
};

const PY_LISTNODE_COMMENT = `# Definition for singly-linked list.
# class ListNode:
#     def __init__(self, val=0, next=None):
#         self.val = val
#         self.next = next
`;

const JS_LISTNODE_COMMENT = `/**
 * Definition for singly-linked list.
 * function ListNode(val, next) {
 *     this.val = (val===undefined ? 0 : val)
 *     this.next = (next===undefined ? null : next)
 * }
 */
`;

function pyType(t: string): string {
  return PY_TYPES[t] ?? 'object';
}

// int[] -> number[], int[][] -> number[][]
function jsType(t: string): string {
  const base = t.replace(/\[\]/g, '');
  const dims = (t.match(/\[\]/g) ?? []).length;
  return (JS_SCALARS[base] ?? 'any') + '[]'.repeat(dims);
}

const CPP_SCALARS: Record<string, string> = {
  int: 'int', long: 'long long', double: 'double', bool: 'bool', string: 'string', char: 'char',
};

// param types: arrays are passed by reference (LeetCode style); ListNode is a pointer.
function cppParamType(t: string): string {
  if (t === 'ListNode') return 'ListNode*';
  if (t.includes('[]')) return cppReturnType(t) + '&';
  return CPP_SCALARS[t] ?? 'auto';
}
// return types: by value, no reference.
function cppReturnType(t: string): string {
  if (t === 'ListNode') return 'ListNode*';
  const base = t.replace(/\[\]/g, '');
  const dims = (t.match(/\[\]/g) ?? []).length;
  if (dims === 0) return CPP_SCALARS[base] ?? 'auto';
  let ty = CPP_SCALARS[base] ?? 'auto';
  for (let d = 0; d < dims; d++) ty = `vector<${ty}>`;
  return ty;
}

const CPP_LISTNODE_COMMENT = `/**
 * Definition for singly-linked list.
 * struct ListNode {
 *     int val;
 *     ListNode *next;
 *     ListNode() : val(0), next(nullptr) {}
 *     ListNode(int x) : val(x), next(nullptr) {}
 *     ListNode(int x, ListNode *next) : val(x), next(next) {}
 * };
 */
`;

function usesType(sig: FunctionSignature, pred: (t: string) => boolean): boolean {
  return [sig.return_type, ...sig.params.map((p) => p.type)].some(pred);
}

function pythonStub(sig: FunctionSignature): string {
  const params = sig.params.map((p) => `${p.name}: ${pyType(p.type)}`).join(', ');
  const ret = pyType(sig.return_type);
  const usesList = usesType(sig, (t) => t.includes('[]'));
  const usesLN = usesType(sig, (t) => t === 'ListNode');
  const imports: string[] = [];
  if (usesList) imports.push('List');
  if (usesLN) imports.push('Optional');
  const header =
    (usesLN ? PY_LISTNODE_COMMENT : '') +
    (imports.length ? `from typing import ${imports.join(', ')}\n\n` : '');
  return `${header}class Solution:\n    def ${sig.function_name}(self, ${params}) -> ${ret}:\n        \n`;
}

function jsStub(sig: FunctionSignature): string {
  const usesLN = usesType(sig, (t) => t === 'ListNode');
  const jsdoc = [
    '/**',
    ...sig.params.map((p) => ` * @param {${jsType(p.type)}} ${p.name}`),
    ` * @return {${jsType(sig.return_type)}}`,
    ' */',
  ].join('\n');
  const names = sig.params.map((p) => p.name).join(', ');
  return `${usesLN ? JS_LISTNODE_COMMENT : ''}${jsdoc}\nvar ${sig.function_name} = function(${names}) {\n    \n};\n`;
}

function cppStub(sig: FunctionSignature): string {
  const usesLN = usesType(sig, (t) => t === 'ListNode');
  const params = sig.params.map((p) => `${cppParamType(p.type)} ${p.name}`).join(', ');
  const ret = cppReturnType(sig.return_type);
  return `${usesLN ? CPP_LISTNODE_COMMENT : ''}class Solution {\npublic:\n    ${ret} ${sig.function_name}(${params}) {\n        \n    }\n};\n`;
}

const JAVA_SCALARS: Record<string, string> = {
  int: 'int', long: 'long', double: 'double', bool: 'boolean', string: 'String', char: 'char',
};

function javaType(t: string): string {
  if (t === 'ListNode') return 'ListNode';
  const base = t.replace(/\[\]/g, '');
  const dims = (t.match(/\[\]/g) ?? []).length;
  return (JAVA_SCALARS[base] ?? 'Object') + '[]'.repeat(dims);
}

const JAVA_LISTNODE_COMMENT = `/**
 * Definition for singly-linked list.
 * public class ListNode {
 *     int val;
 *     ListNode next;
 *     ListNode() {}
 *     ListNode(int val) { this.val = val; }
 *     ListNode(int val, ListNode next) { this.val = val; this.next = next; }
 * }
 */
`;

function javaStub(sig: FunctionSignature): string {
  const usesLN = usesType(sig, (t) => t === 'ListNode');
  const params = sig.params.map((p) => `${javaType(p.type)} ${p.name}`).join(', ');
  const ret = javaType(sig.return_type);
  return `${usesLN ? JAVA_LISTNODE_COMMENT : ''}class Solution {\n    public ${ret} ${sig.function_name}(${params}) {\n        \n    }\n}\n`;
}

const GO_SCALARS: Record<string, string> = {
  int: 'int', long: 'int64', double: 'float64', bool: 'bool', string: 'string', char: 'byte',
};

function goType(t: string): string {
  if (t === 'ListNode') return '*ListNode';
  const base = t.replace(/\[\]/g, '');
  const dims = (t.match(/\[\]/g) ?? []).length;
  return '[]'.repeat(dims) + (GO_SCALARS[base] ?? 'interface{}');
}

const GO_LISTNODE_COMMENT = `/**
 * Definition for singly-linked list.
 * type ListNode struct {
 *     Val int
 *     Next *ListNode
 * }
 */
`;

function goStub(sig: FunctionSignature): string {
  const usesLN = usesType(sig, (t) => t === 'ListNode');
  const params = sig.params.map((p) => `${p.name} ${goType(p.type)}`).join(', ');
  const ret = goType(sig.return_type);
  return `${usesLN ? GO_LISTNODE_COMMENT : ''}func ${sig.function_name}(${params}) ${ret} {\n    \n}\n`;
}

const C_SCALARS: Record<string, string> = {
  int: 'int', long: 'long long', double: 'double', bool: 'bool', string: 'char*', char: 'char',
};

const C_LISTNODE_COMMENT = `/**
 * Definition for singly-linked list.
 * struct ListNode {
 *     int val;
 *     struct ListNode *next;
 * };
 */
`;

function cTypeParts(t: string): { elem: string; dims: number } {
  return { elem: t.replace(/\[\]/g, ''), dims: (t.match(/\[\]/g) ?? []).length };
}

/**
 * Whether the C stub generator and judge driver can express this signature.
 * Mirrors cSupportsSignature() in services/judge/internal/runner/c_harness.go —
 * scalars, 1-D and 2-D arrays of them, and ListNode.
 */
export function supportsC(sig: FunctionSignature): boolean {
  const ok = (t: string) => {
    if (t === 'ListNode') return true;
    const { elem, dims } = cTypeParts(t);
    return elem in C_SCALARS && dims <= 2;
  };
  return ok(sig.return_type) && sig.params.every((p) => ok(p.type));
}

function cValueType(t: string): string {
  if (t === 'ListNode') return 'struct ListNode*';
  const { elem, dims } = cTypeParts(t);
  return (C_SCALARS[elem] ?? 'void*') + '*'.repeat(dims);
}

/**
 * C has no generics and no length-carrying containers, so the signature
 * follows LeetCode's C convention: a size argument after every array
 * parameter, and out-params for an array return. This list must match
 * cParamDecls() in the judge's c_harness.go exactly — the driver calls the
 * function positionally.
 */
function cStub(sig: FunctionSignature): string {
  const parts: string[] = [];
  for (const p of sig.params) {
    parts.push(`${cValueType(p.type)} ${p.name}`);
    if (p.type === 'ListNode') continue;
    const { dims } = cTypeParts(p.type);
    if (dims >= 1) parts.push(`int ${p.name}Size`);
    if (dims === 2) parts.push(`int* ${p.name}ColSize`);
  }
  const { dims: retDims } = cTypeParts(sig.return_type);
  const retIsArray = sig.return_type !== 'ListNode' && retDims >= 1;
  if (retIsArray) {
    parts.push('int* returnSize');
    if (retDims === 2) parts.push('int** returnColumnSizes');
  }

  const usesLN =
    sig.return_type === 'ListNode' || sig.params.some((p) => p.type === 'ListNode');
  const note = retIsArray
    ? '/**\n * Return a malloc\'d array and set *returnSize to its length.\n */\n'
    : '';

  return `${usesLN ? C_LISTNODE_COMMENT : ''}${note}${cValueType(sig.return_type)} ${sig.function_name}(${parts.join(', ')}) {\n    \n}\n`;
}

/** Generate a LeetCode-style function stub. Returns '' for languages without a harness. */
export function generateStub(sig: FunctionSignature, language: string): string {
  if (language === 'python') return pythonStub(sig);
  if (language === 'javascript') return jsStub(sig);
  if (language === 'cpp') return cppStub(sig);
  if (language === 'java') return javaStub(sig);
  if (language === 'go') return goStub(sig);
  if (language === 'c') return supportsC(sig) ? cStub(sig) : '';
  return '';
}
